import { NavigationFailureType, isNavigationFailure } from 'vue-router'
import { App } from '@capacitor/app'

import bus from '@/plugins/eventbus'
import toast from '@/plugins/toasts'
import { stateGetters, stateSetters } from '@/utils/store/store-utils'
import router from '@/plugins/router'

const defaultState = {
  intent: null,
  backButtonHandler: null,
}

export default {
  name: 'native',
  namespaced: true,
  state: () => defaultState,
  getters: {
    ...stateGetters(defaultState),
  },
  mutations: {
    ...stateSetters(defaultState),
  },
  actions: {
    clearBackButtonHandler({ getters, commit }) {
      getters.backButtonHandler?.destroy()
      commit('setBackButtonHandler', null)
    },
    setupBackButtonListener({ commit, dispatch }) {
      dispatch('clearBackButtonHandler')

      // TODO make abstraction layer over this and let listeners subscribe to it
      // (because there's no way to check if there are other listeners here)
      // ionic/vue has a priority system, might be an option but maybe overkill
      const handler = App.addListener('backButton', event => {
        console.log('Native back button detected')
        dispatch('onBackButtonPressed', event)
      })

      commit('setBackButtonHandler', handler)
    },
    onBackButtonPressed({ getters, commit }, event) {
      // default behaviour
      if (event.canGoBack) {
        router.back()
        return
      }

      // close via active intent
      if (getters.intent) {
        console.log('Exiting via intent')

        getters.intent.finish()
        commit('setIntent', null)
        return
      }

      App.exitApp()
    },
    //
    onNewIntent({ commit }, intent) {
      // Doesn't need to do anything else for now
      commit('setIntent', intent)
    },
  },
}

const URL_RE = /(https?:\/\/[^\s]+)/g

// Detect "value is primarily TEXT (with maybe a URL appended)" vs. "value is
// primarily a URL". Used to keep iOS shares like "<selected text> including
// link: <url>" routed to /notes/add instead of /bookmarks/add — same heuristic
// as in src/plugins/share-target.js for Android parity.
function looksLikePrimarilyText(value) {
  if (!value) return false
  const trimmed = value.trim()
  if (!trimmed) return false
  if (/\bincluding link:?\s+https?:\/\//i.test(trimmed)) return true
  const urlMatch = trimmed.match(URL_RE)
  if (!urlMatch) return true
  const url = urlMatch[0]
  if (trimmed === url) return false
  const remainder = trimmed
    .replace(url, '')
    .replace(/\bincluding link:?\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!/[a-zA-Z0-9]/.test(remainder)) return false
  return remainder.length >= 3
}

function stripUrlReferenceFromText(text) {
  if (!text) return ''
  let result = text
  result = result.replace(/\s*\n+\s*(including link:?\s*\n*\s*)?https?:\/\/\S+\s*$/i, '')
  result = result.replace(/\s+including link:?\s+https?:\/\/\S+\s*$/i, '')
  result = result.replace(/\s+https?:\/\/\S+\s*$/, '')
  return result.trim()
}

// iOS share extension sets title = URL absoluteString for non-file URL shares
// (ActionViewController.swift handleTypeUrl). Strip URL-only titles so the
// backend's scraper can supply the real page title — mirrors extractTitle() in
// src/plugins/share-target.js, which the Android path already does.
function extractNonUrlTitle(rawTitle) {
  if (!rawTitle) return null
  const stripped = rawTitle.replaceAll(URL_RE, '').trim()
  return stripped || null
}

// Track recently-handled share URLs to dedupe duplicate appUrlOpen deliveries.
// AppDelegate calls ApplicationDelegateProxy.shared.application(...) twice
// (see ios/App/App/AppDelegate.swift:45-47), which can emit appUrlOpen twice
// for the same URL. Without dedup, the form would auto-submit twice.
const recentlyHandledShareUrls = new Set()

// The iOS share extension double-encodes query values: it pre-applies
// addingPercentEncoding(.urlHostAllowed) on the value BEFORE URLComponents
// re-encodes for the query. URL.searchParams.get decodes only once, so
// non-ASCII text comes back still URL-encoded (e.g. "Kérdés" → "K%C3%A9rd%C3%A9s").
// Decode again, falling back if the result has invalid percent-encoding.
function decodeShareValue(raw) {
  if (!raw) return ''
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

// Detect rubra://?title=...&type=...&url=... URLs from the iOS share extension
// (see ios/App/rubra share extension/ActionViewController.swift sendData()).
// Returns true if the URL was a share intent and was handled.
function handleShareIntentUrl(app, url) {
  if (!url.searchParams.has('title') && !url.searchParams.has('url')) return false

  if (recentlyHandledShareUrls.has(url.href)) {
    console.log('Duplicate share URL, skipping', url.href)
    return true
  }
  recentlyHandledShareUrls.add(url.href)
  setTimeout(() => recentlyHandledShareUrls.delete(url.href), 2000)

  const title = decodeShareValue(url.searchParams.get('title')).trim()
  const sharedUrl = decodeShareValue(url.searchParams.get('url')).trim()
  const type = decodeShareValue(url.searchParams.get('type'))
  if (!title && !sharedUrl) return false

  // PDF data share from iOS Safari's QuickLook viewer arrives as
  // type=application/pdf with a file:// URL pointing into the app-group container
  // (ActionViewController.swift handleTypePdf). The backend can't fetch file:// URLs,
  // so show a clear message instead of falling through to /notes/add (which would
  // silently create a useless note with the filename as content). Sharing via
  // Safari's URL-bar share button takes the public.url path and bookmarks normally.
  if (type === 'application/pdf' && sharedUrl && sharedUrl.startsWith('file://')) {
    toast.error("Can't bookmark PDFs directly yet. Share the page URL from Safari's address bar instead.")
    return true
  }

  // URL share → bookmark add. iOS share extension sets both title and url to the
  // same absoluteString for non-file URLs (handleTypeUrl), so check sharedUrl first.
  if (sharedUrl && /^https?:\/\//i.test(sharedUrl) && !looksLikePrimarilyText(sharedUrl)) {
    const bookmarkTitle = extractNonUrlTitle(title)?.slice(0, 100)
    app.config.globalProperties.$store.dispatch('native/onNewIntent', {
      url: sharedUrl,
      result: { url: sharedUrl, title: bookmarkTitle, type },
      finish: () => {
        app.config.globalProperties.$store.dispatch('native/onNewIntent', null)
      },
    })
    router.replace('/bookmarks/add').catch(() => {})
    return true
  }

  // Title may contain a URL too (e.g. user shared just a URL string from a non-Safari source);
  // route to bookmark add in that case — but only when title is *primarily* a URL,
  // not text + URL (which is a text-selection share masquerading as a URL share).
  const urlInTitle = title.match(URL_RE)?.[0]
  if (urlInTitle && !looksLikePrimarilyText(title)) {
    const bookmarkTitle = extractNonUrlTitle(title)?.slice(0, 100)
    app.config.globalProperties.$store.dispatch('native/onNewIntent', {
      url: urlInTitle,
      result: { url: urlInTitle, title: bookmarkTitle, type },
      finish: () => {
        app.config.globalProperties.$store.dispatch('native/onNewIntent', null)
      },
    })
    router.replace('/bookmarks/add').catch(() => {})
    return true
  }

  // Plain text share (or text + URL → strip the URL) → note add.
  // Matches share-target.js's text-only path on Android.
  const sourceText = sharedUrl && looksLikePrimarilyText(sharedUrl) ? sharedUrl : title
  const cleanedText = stripUrlReferenceFromText(sourceText) || sourceText.trim()
  if (cleanedText) {
    const noteTitle = cleanedText.slice(0, 100)
    app.config.globalProperties.$store.dispatch('native/onNewIntent', {
      type: 'note',
      text: cleanedText,
      title: noteTitle,
      result: { title: noteTitle, text: cleanedText, type: 'text/plain' },
      finish: () => {
        app.config.globalProperties.$store.dispatch('native/onNewIntent', null)
      },
    })
    router.replace('/notes/add').catch(() => {})
    return true
  }

  return false
}

// Use mitt's 'on' method instead of Vue 2's $once
bus.on('app:created', async app => {
  if (app.config.globalProperties.$isNative) {
    app.config.globalProperties.$store.dispatch('native/setupBackButtonListener')

    // Cold-launch share: if the app was just launched by tapping Rubra in the
    // share sheet, the URL won't fire appUrlOpen (the listener registers below
    // AFTER launch). App.getLaunchUrl() returns it explicitly.
    try {
      const launchUrlResult = await App.getLaunchUrl()
      if (launchUrlResult?.url) {
        const launchUrl = new URL(launchUrlResult.url)
        if (handleShareIntentUrl(app, launchUrl)) {
          return
        }
      }
    } catch (err) {
      console.warn('Could not handle launch URL', err)
    }

    // handle deeplinks
    // NOTE this has buggy behaviour when running with `yarn dev:android`
    // probably because the app is never really killed, building app via Android Studio shows normal behaviour


    App.addListener('appUrlOpen', event => {
      console.log('App was opened from external url', event.url)
      let url
      try {
        url = new URL(event.url) // this also works with app url schemes, ex. com.rubra.appclient
      } catch (err) {
        console.warn('Could not parse appUrlOpen URL', event.url, err)
        return
      }

      // iOS share-extension delivers shared content via rubra:// URL scheme with
      // title/description/type/url query params. Handle this directly instead of
      // relying on the SendIntent plugin's notification → bridge.eval → window-event
      // chain, which is unreliable on iOS 26+ when the app is already running.
      if (handleShareIntentUrl(app, url)) return

      if (!url.pathname) {
        console.warn('No pathname found in url, ignoring...')
        return
      }

      router.push(url.pathname + url.search).catch(err => {
        // Only duplicated navigations
        if (isNavigationFailure(err, NavigationFailureType.duplicated)) {
          // Use mitt's emit method instead of Vue 2's $emit
          bus.emit('router:error:duplicated')
        } else {
          console.error(err)
        }
      })
    })
  }
})
